import { PropertyFeeFormRow } from '../types';

/** Today's date in mm/dd/yyyy for new fee rows. */
export const todayFeeDate = (): string => {
  const now = new Date();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const year = now.getFullYear();
  return `${month}/${day}/${year}`;
};

/** Uniqueness key within a property: type + contact + interval. */
export const feeUniquenessKey = (fee: Pick<PropertyFeeFormRow, 'typeId' | 'contactId' | 'interval'>): string =>
  `${fee.typeId}|${fee.contactId}|${fee.interval}`;

/** Sortable timestamp from mm/dd/yyyy (or ISO YYYY-MM-DD). */
export const feeDateSortValue = (feeDate: string): number => {
  if (!feeDate) return 0;
  if (/^\d{4}-\d{2}-\d{2}/.test(feeDate)) {
    const t = Date.parse(feeDate.slice(0, 10));
    return Number.isNaN(t) ? 0 : t;
  }
  const parts = feeDate.split('/');
  if (parts.length !== 3) return 0;
  const month = Number(parts[0]);
  const day = Number(parts[1]);
  const year = Number(parts[2]);
  const t = new Date(year, month - 1, day).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const typeSortName = (fee: PropertyFeeFormRow): string =>
  (fee.typeName || `Type #${fee.typeId}`).toLocaleLowerCase();

/** Keep the newest feeDate per (typeId, contactId, interval); ties prefer higher id. */
export const selectLatestFees = (fees: PropertyFeeFormRow[]): PropertyFeeFormRow[] => {
  const latest = new Map<string, PropertyFeeFormRow>();
  for (const fee of fees) {
    const key = feeUniquenessKey(fee);
    const current = latest.get(key);
    if (!current) {
      latest.set(key, fee);
      continue;
    }
    const feeTime = feeDateSortValue(fee.feeDate);
    const currentTime = feeDateSortValue(current.feeDate);
    if (feeTime > currentTime || (feeTime === currentTime && (fee.id || 0) > (current.id || 0))) {
      latest.set(key, fee);
    }
  }
  return Array.from(latest.values());
};

export const sortFeesByTypeAscending = (fees: PropertyFeeFormRow[]): PropertyFeeFormRow[] =>
  [...fees].sort((a, b) => {
    const byType = typeSortName(a).localeCompare(typeSortName(b));
    if (byType !== 0) return byType;
    const byDate = feeDateSortValue(b.feeDate) - feeDateSortValue(a.feeDate);
    if (byDate !== 0) return byDate;
    return (b.id || 0) - (a.id || 0);
  });

export interface PropertyFeeListItem {
  fee: PropertyFeeFormRow;
  /** Index in the full unsaved propertiesFees array */
  sourceIndex: number;
}

/** Apply show-latest filter and type sort; preserve source indexes for edit/delete. */
export const getVisiblePropertyFees = (
  fees: PropertyFeeFormRow[],
  showLatest: boolean
): PropertyFeeListItem[] => {
  const indexed = fees.map((fee, sourceIndex) => ({ fee, sourceIndex }));

  const filtered = showLatest
    ? (() => {
        const latestByKey = new Map<string, PropertyFeeListItem>();
        for (const item of indexed) {
          const key = feeUniquenessKey(item.fee);
          const current = latestByKey.get(key);
          if (!current) {
            latestByKey.set(key, item);
            continue;
          }
          const feeTime = feeDateSortValue(item.fee.feeDate);
          const currentTime = feeDateSortValue(current.fee.feeDate);
          if (
            feeTime > currentTime ||
            (feeTime === currentTime && (item.fee.id || 0) > (current.fee.id || 0))
          ) {
            latestByKey.set(key, item);
          }
        }
        return Array.from(latestByKey.values());
      })()
    : indexed;

  return filtered.sort((a, b) => {
    const byType = typeSortName(a.fee).localeCompare(typeSortName(b.fee));
    if (byType !== 0) return byType;
    const byDate = feeDateSortValue(b.fee.feeDate) - feeDateSortValue(a.fee.feeDate);
    if (byDate !== 0) return byDate;
    return (b.fee.id || 0) - (a.fee.id || 0);
  });
};
